import {
  Star,
  MagnifyingGlassPlus,
  Heartbeat,
  FirstAid,
  SignIn,
  TrendUp,
  CurrencyDollarSimple,
  Receipt,
  PlusMinus,
  Lightbulb,
  IconProps,
  CaretRight,
} from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import { useQuery } from 'react-query';

import { isValidHttpsUrl, api } from 'theSource/utils';
import { mapKeyActionDetails, ContentBlock } from './utils';
import { KeyActionDto } from './types';
import {
  ClientAttributesDto,
  useSubscriberQuery,
  IntegrationsResponseDto,
} from 'theSource/queries';
import {
  KeyActionsTitleStyled,
  ListItemButtonStyled,
  ListItemIconDefaultStyled,
  ListItemIconCircleStyled,
  ListItemTextStyled,
  ListItemStyled,
  FormattedQuestion,
} from './KeyActionsStyled';

export interface KeyActionsProps {
  keyActions?: KeyActionDto;
  integrations?: IntegrationsResponseDto;
  carrierLookupCode?: string;
}

export interface IconClassProps {
  iconClass: string;
  iconColor: string;
}

export const components: {
  [key: string]: React.ForwardRefExoticComponent<
    IconProps & React.RefAttributes<SVGSVGElement>
  >;
} = {
  'ph-lightbulb': Lightbulb,
  'ph-star': Star,
  'ph-magnifying-glass-plus': MagnifyingGlassPlus,
  'ph-heartbeat': Heartbeat,
  'ph-first-aid': FirstAid,
  'ph-sign-in': SignIn,
  'ph-trend-up': TrendUp,
  'ph-currency-dollar-simple': CurrencyDollarSimple,
  'ph-receipt': Receipt,
  'ph-plus-minus': PlusMinus,
};

export function SpecificIcon(props: IconClassProps) {
  if (props.iconClass === 'question') {
    return (
      <FormattedQuestion>
        <span>?</span>
      </FormattedQuestion>
    );
  }
  const SpecificIcon = components[props.iconClass];
  if (SpecificIcon) {
    return <SpecificIcon size={20} color={props.iconColor} />;
  }
  return <Star size={20} color={props.iconColor} />;
}

export function postRedirectData(
  ssoProtocol?: string,
  carrierLookupCode?: string,
  subscriberId?: number,
  organizationId?: number,
  orgCode?: string
) {
  const payload = {
    sso_protocol: ssoProtocol,
    carrier_lookup_code: carrierLookupCode,
    subscriber_id: subscriberId,
    org_id: organizationId,
    org_code: orgCode,
  };
  api.post('/post_redirect_data', payload);
}

export function handleClick(
  keyAction?: ContentBlock,
  clientAttributes?: ClientAttributesDto,
  subscriberId?: number,
  organizationId?: number,
  orgCode?: string
) {
  const path = keyAction?.url;
  const isLoginAction = keyAction?.slotNum === 1;

  if (path && isValidHttpsUrl(path!)) {
    window.open(path!, '_blank');
  } else if (isLoginAction) {
    const ssoProtocol = clientAttributes?.sso_protocol;
    const carrierLookupAttribute = clientAttributes?.carrier_lookup_code;
    postRedirectData(
      ssoProtocol,
      carrierLookupAttribute,
      subscriberId,
      organizationId,
      orgCode
    );
    window.open(
      `/v1/self_service/redirect_to_carrier?carrier_lookup_code=${carrierLookupAttribute}`,
      '_blank'
    );
  }
}

export const KeyActions = ({
  keyActions,
  integrations,
  carrierLookupCode,
}: KeyActionsProps) => {
  const { t } = useTranslation('pages.benefits');
  const theme = useTheme();

  const { subscriber, isLoadingSubscriber, isFetchingSubscriber } =
    useSubscriberQuery({ restrictAdmin: true });

  const integration = Array.prototype.find.call(
    integrations || [],
    (integration) => integration.carrier_lookup_code === carrierLookupCode
  );

  const {
    data: clientAttributesData,
    isLoading: isLoadingClientAttributes,
    isFetching: isFetchingClientAttributes,
  } = useQuery<ClientAttributesDto>('get-client-attributes', () =>
    api.get<ClientAttributesDto>(
      `client_attributes?carrier_lookup_id=${integration?.carrier_lookup_id}`
    )
  );

  const subscriberId = subscriber?.data.id.value as number;
  const organizationId = subscriber?.data.organization_id.value as number;
  const orgCode = subscriber?.data.organization_code.value as string;

  const detailsExist =
    keyActions &&
    keyActions.data.content_blocks &&
    keyActions.data.content_blocks?.length > 0;

  const currentKeyActionDetails =
    keyActions && mapKeyActionDetails(keyActions.data.content_blocks);

  return (
    <>
      {detailsExist && (
        <KeyActionsTitleStyled data-testid="key-actions" className="headline-3">
          {t('keyActions')}
        </KeyActionsTitleStyled>
      )}

      {currentKeyActionDetails?.map(
        (item, index) =>
          !isLoadingSubscriber &&
          !isFetchingSubscriber &&
          !isLoadingClientAttributes &&
          !isFetchingClientAttributes &&
          (item.slotNum === 1 || item.url !== null) && (
            <ListItemStyled data-testid="key-action-list-item">
              <ListItemButtonStyled
                key={index}
                onClick={() =>
                  handleClick(
                    item,
                    clientAttributesData,
                    subscriberId,
                    organizationId,
                    orgCode
                  )
                }
              >
                <ListItemIconCircleStyled>
                  <SpecificIcon
                    iconClass={item.iconClass}
                    iconColor={theme.palette.common.white}
                  />
                </ListItemIconCircleStyled>
                <ListItemTextStyled
                  primaryTypographyProps={{
                    variant: 'body2',
                  }}
                  primary={item.label}
                />
                <ListItemIconDefaultStyled>
                  <CaretRight
                    size={20}
                    weight="bold"
                    color={theme.palette.primary.main}
                  />
                </ListItemIconDefaultStyled>
              </ListItemButtonStyled>
            </ListItemStyled>
          )
      )}
    </>
  );
};
